/**
 * Small status vocabulary for the Studio system.
 *
 * StatusText: text with a leading 6px dot. Used for masthead chips and table
 * status cells. Becomes a quiet link when given `to`.
 * Pill: a small tinted pill for states that need a touch more weight.
 *
 * Status colour never fills a surface. It is a dot or an 8 percent tint.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { Tone } from "./ListRow";

export interface StatusTextProps {
  children: ReactNode;
  tone?: Tone;
  to?: string;
  onClick?: () => void;
}

export function StatusText({ children, tone = "neutral", to, onClick }: StatusTextProps) {
  const inner = (
    <>
      <span className="st-status__dot" data-tone={tone} aria-hidden />
      {children}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="st-status">
        {inner}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" className="st-status" onClick={onClick}>
        {inner}
      </button>
    );
  }
  return <span className="st-status">{inner}</span>;
}

export interface PillProps {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}

export function Pill({ children, tone = "neutral", dot = false }: PillProps) {
  return (
    <span className="st-pill" data-tone={tone === "neutral" ? undefined : tone}>
      {dot ? <span className="st-pill__dot" aria-hidden /> : null}
      {children}
    </span>
  );
}
